import { type OrganizationList, type Pagination, type PaginationData, createOrganizationList } from 'models';
import { useInfiniteQuery } from 'query';
import type { ApiClientResponseError, DefaultResponseErrorDataObject } from 'common/services';
import type { OrganizationScope } from '../models';
import { getOrganizations } from '../services';
import { getOrganizationsInfiniteQueryKey } from './keys';

export type OrganizationsListFilter = {
  communityId?: string;
  groupId?: string;
  order?: 'channels';
  scope?: OrganizationScope;
  search?: string;
  userId?: string;
};

type P = {
  filter?: OrganizationsListFilter;
  pagination?: Omit<Pagination, 'position'>;
};

export const useOrganizationsInfiniteQuery = ({ filter, pagination }: P) =>
  useInfiniteQuery<
    PaginationData<OrganizationList>,
    string | undefined,
    ApiClientResponseError<DefaultResponseErrorDataObject>
  >({
    async queryFn({ pageParam: cursor }) {
      /* eslint-disable @typescript-eslint/naming-convention -- Start: required by backend API */
      const response = await getOrganizations({
        config: {
          params: {
            cursor,
            group_id: filter?.groupId,
            limit: pagination?.pageSize,
            order: filter?.order,
            scope: filter?.scope,
            search: filter?.search,
            user_id: filter?.userId,
          },
        },
        /* eslint-enable @typescript-eslint/naming-convention -- End: required by backend API */
      });

      return {
        data: response.data.data.map((organization) => createOrganizationList(organization)),
        total: response.data.total,
      };
    },
    initialPageParam: undefined,
    getNextPageParam(lastPage, allPages) {
      if (!pagination?.pageSize) {
        return undefined;
      }

      if (allPages.length * pagination.pageSize >= lastPage.total) {
        return undefined;
      }

      return lastPage.data.at(-1)?.id;
    },
    queryKey: getOrganizationsInfiniteQueryKey({ filter, pagination }),
  });
